import { Component, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { Observable, Subject } from 'rxjs';
import { SimpleAutocomplete, SimpleAutocompleteGroupData, SimpleAutocompleteOption, SimpleAutocompleteSelection, SimpleAutocompleteTab } from '../../directives/simpleAutocomplete';

@Component({
  selector: 'simple-autocomplete-overlay',
  templateUrl: 'simple-autocomplete-overlay.component.html',
  styleUrls: ["./simple-autocomplete-overlay.component.scss"]
})
export class SimpleAutocompleteOverlay<T = any> {
  @ViewChildren('header') headerElements: QueryList<ElementRef>;
  @ViewChildren('option') optionButtons: QueryList<MatButton>;

  private autocomlete: SimpleAutocomplete;
  private selectionSubject$: Subject<SimpleAutocompleteSelection<T>>;
  readonly selections$: Observable<SimpleAutocompleteSelection<T>>;

  constructor() {
    this.autocomlete = null;
    this.selectionSubject$ = new Subject<SimpleAutocompleteSelection<T>>();
    this.selections$ = this.selectionSubject$.asObservable();
  }

  setAutocomplete(autocomlete: SimpleAutocomplete<T>): void {
    this.autocomlete = autocomlete;
  }

  overlayIsHidden(): boolean {
    return !this.autocomlete?.getSelectedTab();
  }

  getTabs(): SimpleAutocompleteTab<T>[] {
    return this.autocomlete?.getTabs() || [];
  }

  getGroupNames(): string[] {
    return this.autocomlete?.getSelectedTab()?.getGroupNames() || [];
  }

  getGroupData(groupName: string): SimpleAutocompleteGroupData {
    return this.autocomlete?.getSelectedTab()?.getGroupData(groupName);
  }

  groupIsCollapsed(groupName: string): boolean {
    return this.getGroupData(groupName)?.expanded == false;
  }

  getGroupOptions(groupName: string): SimpleAutocompleteOption<T>[] {
    return this.getGroupData(groupName)?.options || [];
  }

  headerIsHidden(groupName: string): boolean {
    return !(groupName?.length > 0);
  }

  headerIsSelected(groupName: string): boolean {
    return groupName?.length > 0 && this.autocomlete?.getSelectedTab()?.getSelectedGroupHeaderName() == groupName;
  }

  toggleGroupExpanded(groupName: string): void {
    this.autocomlete?.getSelectedTab()?.toggleExpanded(groupName);
  }

  tabIsSelected(tab: SimpleAutocompleteTab<T>): boolean {
    return this.autocomlete?.tabIsSelected(tab) || false;
  }

  tabIsHidden(tab: SimpleAutocompleteTab<T>): boolean {
    return tab.isHidden();
  }

  selectTab(tab: SimpleAutocompleteTab<T>): void {
    this.autocomlete?.selectTab(tab);
  }

  optionIsSelected(option: SimpleAutocompleteOption<T>): boolean {
    return this.autocomlete?.getSelectedTab()?.optionIsSelected(option) || false;
  }

  chooseOption(option: SimpleAutocompleteOption<T>): void {
    this.selectionSubject$.next({
      tab: this.autocomlete.getSelectedTab(),
      option: option
    });
  }

  private getOptionElementId(optionId: number): string {
    return 'simple_autocomplete_option_' + optionId;
  }

  private autoscroll(): void {
    const tab: SimpleAutocompleteTab<T> = this.autocomlete?.getSelectedTab();
    if(!tab)
      return;
    if(tab.getSelectedGroupHeaderName()) {
      const index: number = this.getGroupNames().indexOf(tab.getSelectedGroupHeaderName());
      if(index >= 0 && this.headerElements) {
        const headerElement: ElementRef = this.headerElements.toArray()[index];
        headerElement?.nativeElement.scrollIntoViewIfNeeded();
      }
      return;
    }
    if(tab.getSelectedOption())
      this.optionButtons?.toArray().find((button: MatButton): boolean => button._elementRef.nativeElement.id == this.getOptionElementId(tab.getSelectedOption().id))?._elementRef.nativeElement.scrollIntoViewIfNeeded();
  }

  private selectPreviousGroup(tab: SimpleAutocompleteTab<T>, groupName?: string): void {
    const groupNames: string[] = this.getGroupNames();
    if(groupNames.length == 0)
      return;
    const groupIndex: number = groupName == null ? -1 : groupNames.indexOf(groupName);
    const prevGroup: SimpleAutocompleteGroupData = tab.getGroupData(groupNames[groupIndex < 1 ? groupNames.length - 1 : groupIndex - 1]);
    if(!prevGroup.expanded) {
      tab.selectGroup(prevGroup.name);
      this.autoscroll();
      return;
    }
    tab.selectOption(prevGroup.options[prevGroup.options.length - 1]);
    this.autoscroll();
  }

  private selectNextGroup(tab: SimpleAutocompleteTab<T>, groupName?: string): void {
    const groupNames: string[] = this.getGroupNames();
    if(groupNames.length == 0)
      return;
    const groupIndex: number = groupName == null ? -1 : groupNames.indexOf(groupName);
    const nextGroup: SimpleAutocompleteGroupData = tab.getGroupData(groupNames[groupIndex < 0 ? 0 : (groupIndex + 1) % groupNames.length]);
    if(this.headerIsHidden(nextGroup.name)) {
      tab.selectOption(nextGroup.options[0]);
      this.autoscroll();
      return;
    }
    tab.selectGroup(nextGroup.name);
    this.autoscroll();
  }

  up(): void {
    const tab: SimpleAutocompleteTab<T> = this.autocomlete?.getSelectedTab();
    if(!tab)
      return;
    const selectedGroupHeaderName: string = tab.getSelectedGroupHeaderName();
    if(selectedGroupHeaderName) {
      this.selectPreviousGroup(tab, selectedGroupHeaderName);
      return;
    }
    const selectedOption: SimpleAutocompleteOption<T> = tab.getSelectedOption();
    if(selectedOption) {
      const options: SimpleAutocompleteOption<T>[] = (this.getGroupData(selectedOption.group)?.options || []);
      const optionIndex: number = options.findIndex((option: SimpleAutocompleteOption<T>): boolean => tab.optionIsSelected(option));
      if(optionIndex < 1) {
        if(this.headerIsHidden(selectedOption.group)) {
          this.selectPreviousGroup(tab, selectedOption.group);
          return;
        }
        tab.selectGroup(selectedOption.group);
        this.autoscroll();
        return;
      }
      tab.selectOption(options[optionIndex - 1]);
      this.autoscroll();
      return;
    }
    this.selectPreviousGroup(tab);
  }

  down(): void {
    const tab: SimpleAutocompleteTab<T> = this.autocomlete?.getSelectedTab();
    if(!tab)
      return;
    const selectedGroupHeaderName: string = tab.getSelectedGroupHeaderName();
    if(selectedGroupHeaderName) {
      if(this.groupIsCollapsed(selectedGroupHeaderName)) {
        this.selectNextGroup(tab, selectedGroupHeaderName);
        return;
      }
      tab.selectOption((tab.getGroupData(selectedGroupHeaderName)?.options || [])[0]);
      this.autoscroll()
      return;
    }
    const selectedOption: SimpleAutocompleteOption<T> = tab.getSelectedOption();
    if(selectedOption) {
      const options: SimpleAutocompleteOption<T>[] = (this.getGroupData(selectedOption.group)?.options || []);
      const optionIndex: number = options.findIndex((option: SimpleAutocompleteOption<T>): boolean => tab.optionIsSelected(option));
      if(optionIndex == options.length - 1) {
        this.selectNextGroup(tab, selectedOption.group);
        return;
      }
      tab.selectOption(options[optionIndex + 1]);
      this.autoscroll();
      return;
    }
    this.selectNextGroup(tab);
  }

  submit(): void {
    const tab: SimpleAutocompleteTab<T> = this.autocomlete?.getSelectedTab();
    if(!tab)
      return;
    const selectedGroupHeaderName: string = tab.getSelectedGroupHeaderName();
    if(selectedGroupHeaderName) {
      tab.toggleExpanded(selectedGroupHeaderName);
      return;
    }
    const selectedOption: SimpleAutocompleteOption<T> = tab?.getSelectedOption();
    if(selectedOption) {
      this.selectionSubject$.next({
        tab: tab,
        option: selectedOption
      });
    }
  }

  handleMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }
}